import { ArrowDown, ArrowUp, Columns3, HelpCircle, ListFilter, Lock, Merge, Pencil, Search, Trash2, UserPlus, X } from 'lucide-react';
import { useDeferredValue, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ageOf, ageText, inDays, nextAnniversary } from '../../model/countdown';
import { dateSortKey, formatDate, parseUserDate } from '../../model/dates';
import { graphOf } from '../../model/graph';
import { relativesOf, type KinColumn } from '../../model/relatives';
import { headlineRelations, joinNames, relate } from '../../model/kinship';
import { birthDate, comparePeople, deathDate, deathEvent, defaultPronouns, displayName, findEvent, genderLabel, GENDER_LABELS, isDeceased, latestEvent, primaryName, shortName, withEventField } from '../../model/people';
import { FIELD_LABEL, normalize, search, SEARCH_HELP, type SearchResult } from '../../model/search';
import { removePeople, upsertPerson } from '../../model/tree';
import type { GenderIdentity, Id, Person, Tree } from '../../model/types';
import { lsGet, lsSet } from '../../model/db';
import { Avatar, Empty } from '../components/common';
import { GenderPicker } from '../components/GenderPicker';
import { GroupsEditor, KinEditor } from '../components/LinkEditors';
import { PlaceInput } from '../components/PlaceInput';
import { PronounPicker } from '../components/PronounPicker';
import { Menu, type MenuEntry } from '../components/Menu';
import { openContextMenu } from '../components/ContextMenu';
import { Popover, usePopover } from '../components/Popover';
import { plural } from '../labels';
import { font, lineCount, textWidth } from './tableLayout';
import { commit, confirmAction, getState, openDialog, setState, showPerson, toast, undo, useApp } from '../store';

type ColKey =
  | 'relation'
  | 'nickname'
  | 'born'
  | 'birthplace'
  | 'died'
  | 'deathplace'
  | 'age'
  | 'birthday'
  | 'gender'
  | 'pronouns'
  | 'occupation'
  | 'residence'
  | 'parents'
  | 'partners'
  | 'siblings'
  | 'children'
  | 'friends'
  | 'groups'
  | 'tags'
  | 'notes';
type SortKey = 'relevance' | 'name' | ColKey;

/** min/max: the range a column's width is fitted into (text beyond max wraps). */
const COLUMNS: { key: ColKey; label: string; editable: boolean; min: number; max: number; title?: string }[] = [
  { key: 'relation', label: 'Relationship', editable: false, min: 140, max: 260, title: 'How they’re related to the home person' },
  { key: 'nickname', label: 'Nickname', editable: true, min: 90, max: 160 },
  { key: 'born', label: 'Born', editable: true, min: 80, max: 150 },
  { key: 'birthplace', label: 'Birthplace', editable: true, min: 100, max: 260 },
  { key: 'died', label: 'Died', editable: true, min: 80, max: 150 },
  { key: 'deathplace', label: 'Death place', editable: true, min: 110, max: 260 },
  { key: 'age', label: 'Age', editable: false, min: 60, max: 100 },
  { key: 'birthday', label: 'Next birthday', editable: false, min: 110, max: 190 },
  { key: 'gender', label: 'Gender', editable: true, min: 90, max: 200 },
  { key: 'pronouns', label: 'Pronouns', editable: true, min: 90, max: 160 },
  { key: 'occupation', label: 'Occupation', editable: true, min: 100, max: 240 },
  { key: 'residence', label: 'Lives in', editable: true, min: 90, max: 240, title: 'Most recent residence' },
  { key: 'parents', label: 'Parents', editable: true, min: 90, max: 260 },
  { key: 'partners', label: 'Partners', editable: true, min: 90, max: 260 },
  { key: 'siblings', label: 'Siblings', editable: true, min: 90, max: 260 },
  { key: 'children', label: 'Children', editable: true, min: 90, max: 260 },
  { key: 'friends', label: 'Friends', editable: true, min: 90, max: 260, title: 'Friends, close friends, and best friends' },
  { key: 'groups', label: 'Groups', editable: true, min: 100, max: 240 },
  { key: 'tags', label: 'Tags', editable: true, min: 70, max: 240 },
  { key: 'notes', label: 'Notes', editable: true, min: 170, max: 560 },
];
/** Columns whose text wraps well, so they give up width first when space is tight. */
const FLEXIBLE: ColKey[] = ['relation', 'birthplace', 'deathplace', 'occupation', 'residence', 'parents', 'partners', 'siblings', 'children', 'friends', 'groups', 'tags', 'notes'];
/** Columns that list other people by name. */
const KIN: KinColumn[] = ['parents', 'partners', 'siblings', 'children', 'friends'];
/** Where the Columns menu puts a divider, between related columns. */
const MENU_BREAKS: ColKey[] = ['born', 'gender', 'occupation', 'parents', 'tags'];
const NOTES_PREFERRED = 260;
const READ_ONLY_HINT = 'Worked out from the tree, so it can’t be edited here';
// Working out everyone's relationship to the home person is slow on big trees, so the
// Relationship column isn't fitted to its data; it starts at this width and wraps.
const RELATION_PREFERRED = 170;
const CELL_PAD = 24; // td padding, left + right
const CHECK_W = 38; // the selection checkboxes
// Chip widths beyond their text: avatar, gap, padding, and border; and the space between chips.
const PERSON_CHIP = 20 + 5 + 1 + 9 + 2;
const GROUP_CHIP = 18 + 2;
const CHIP_GAP = 4;
const NAME_EXTRA = 26 + 10; // avatar + gap
const LINE_H = 13.5 * 1.45;
const DEFAULT_COLUMNS: ColKey[] = ['born', 'birthplace', 'died', 'gender', 'pronouns'];
const COLS_KEY = 'relationships.peopleColumns';

function loadColumns(): ColKey[] {
  try {
    const saved = JSON.parse(lsGet(COLS_KEY) ?? 'null');
    if (Array.isArray(saved)) return COLUMNS.map((c) => c.key).filter((k) => saved.includes(k));
  } catch {
    /* fall through */
  }
  return DEFAULT_COLUMNS;
}

const ROW_H = 45;

interface HomeRelation {
  term: string;
  /** For sorting: closest first. */
  rank: number;
  steps: number;
}
// Cached per tree object (a new tree after any edit) and home person.
const relationCache = new WeakMap<Tree, Map<string, HomeRelation | null>>();
function homeRelation(tree: Tree, id: Id): HomeRelation | null {
  const home = tree.homePersonId;
  if (!home || !tree.people[home]) return null;
  let cache = relationCache.get(tree);
  if (!cache) relationCache.set(tree, (cache = new Map()));
  const key = `${home}|${id}`;
  if (cache.has(key)) return cache.get(key)!;
  const lead = headlineRelations(relate(tree, home, id));
  const rel = lead[0];
  // Every direct tie is listed ("best friend and chosen sister"); sorting goes by the closest.
  const value = !rel ? null : rel.category === 'self' ? { term: 'Home person', rank: -1, steps: 0 } : { term: joinNames(lead.map((r) => r.term)), rank: rel.rank, steps: rel.path.length };
  cache.set(key, value);
  return value;
}

const names = (tree: Tree, ids: Id[]) => ids.map((id) => shortName(tree.people[id])).join(', ');
const isKin = (key: ColKey | 'name'): key is KinColumn => (KIN as string[]).includes(key);
function kin(tree: Tree, p: Person, key: ColKey): Id[] {
  return isKin(key) ? relativesOf(tree, p.id, key).map((r) => r.id) : [];
}

/** Text shown in a cell, also used for sorting and as the editor's starting value. */
function cellText(tree: Tree, p: Person, key: ColKey): string {
  switch (key) {
    case 'relation': return homeRelation(tree, p.id)?.term ?? '';
    case 'nickname': return primaryName(p)?.nickname ?? '';
    case 'birthday': {
      const b = !isDeceased(p) ? nextAnniversary(birthDate(p)) : undefined;
      return b ? `${inDays(b.days)} · turns ${b.count}` : '';
    }
    case 'occupation': return latestEvent(p, 'occupation')?.label ?? '';
    case 'residence': return latestEvent(p, 'residence')?.place ?? '';
    case 'parents':
    case 'partners':
    case 'siblings':
    case 'children':
    case 'friends':
      return names(tree, kin(tree, p, key));
    case 'groups': return (graphOf(tree).groupsOf.get(p.id) ?? []).map((g) => g.name).join(', ');
    case 'born': return formatDate(birthDate(p));
    case 'birthplace': return findEvent(p, 'birth')?.place ?? '';
    case 'died': return formatDate(deathDate(p));
    case 'deathplace': return deathEvent(p)?.place ?? '';
    case 'age': {
      const a = ageOf(p);
      return a ? (a.atDeath ? `d. at ${ageText(a)}` : ageText(a)) : '';
    }
    case 'gender': return p.gender.identity !== 'unknown' ? genderLabel(p.gender) : '';
    case 'pronouns': return p.pronouns ?? '';
    case 'tags': return p.tags.join(', ');
    case 'notes': return p.notes;
  }
}

function applyEdit(p: Person, key: ColKey | 'name', value: string): Person {
  switch (key) {
    case 'born': return withEventField(p, 'birth', 'date', parseUserDate(value).value || undefined);
    case 'died': return withEventField(p, 'death', 'date', parseUserDate(value).value || undefined);
    case 'birthplace': return withEventField(p, 'birth', 'place', value.trim());
    case 'deathplace': return withEventField(p, 'death', 'place', value.trim());
    case 'occupation': return withEventField(p, 'occupation', 'label', value.trim());
    case 'residence': return withEventField(p, 'residence', 'place', value.trim());
    case 'nickname': {
      const n = primaryName(p);
      const nickname = value.trim() || undefined;
      if (!n) return nickname ? { ...p, names: [{ given: '', surname: '', type: 'name', nickname }, ...p.names] } : p;
      return { ...p, names: p.names.map((x) => (x === n ? { ...x, nickname } : x)) };
    }
    case 'pronouns': return { ...p, pronouns: value.trim() || undefined };
    case 'tags': return { ...p, tags: [...new Set(value.split(',').map((t) => t.trim()).filter(Boolean))] };
    case 'notes': return { ...p, notes: value };
    default: return p;
  }
}

export function PeopleView() {
  const tree = useApp((s) => s.tree)!;
  // The highlighted row is local: the person sidebar belongs to the charts.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const query = useApp((s) => s.peopleQuery);
  const [status, setStatus] = useState<'' | 'living' | 'deceased' | 'unknown'>('');
  const [gender, setGender] = useState('');
  const [bornFrom, setBornFrom] = useState('');
  const [bornTo, setBornTo] = useState('');
  /** Group id and exact tag chosen in the filter menus. */
  const [group, setGroup] = useState('');
  const [tag, setTag] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'relevance', dir: 1 });
  const [help, setHelp] = useState(false);
  const [columns, setColumns] = useState<ColKey[]>(loadColumns);
  const [colMenu, setColMenu] = useState<{ x: number; y: number } | null>(null);
  const [editing, setEditing] = useState<{ id: string; key: ColKey | 'name' } | null>(null);
  // People ticked for bulk actions. Kept while the search changes, so you can gather people from several searches.
  const [checked, setChecked] = useState<ReadonlySet<Id>>(() => new Set());
  const anchor = useRef<Id | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const allTags = useMemo(() => [...new Set(Object.values(tree.people).flatMap((p) => p.tags))].sort(), [tree]);
  const groups = useMemo(() => Object.values(tree.groups).sort((a, b) => a.name.localeCompare(b.name)), [tree]);
  const hasHome = !!tree.homePersonId && !!tree.people[tree.homePersonId];

  const toggleColumn = (key: ColKey) => {
    const next = columns.includes(key) ? columns.filter((k) => k !== key) : COLUMNS.map((c) => c.key).filter((k) => k === key || columns.includes(k));
    setColumns(next);
    lsSet(COLS_KEY, JSON.stringify(next));
  };

  const fullQuery = [
    query,
    status && `is:${status}`,
    gender && `gender:${gender}`,
    (bornFrom || bornTo) && `born:${bornFrom || '0'}..${bornTo || '9999'}`,
  ]
    .filter(Boolean)
    .join(' ');
  const deferred = useDeferredValue(fullQuery);
  const results: SearchResult[] = useMemo(() => {
    const found = !deferred.trim() ? Object.values(tree.people).sort(comparePeople).map((person) => ({ person, score: 0, matches: [] })) : search(tree, deferred);
    // The group and tag menus pick one exactly. (Typed group: and tag: filters match part of a
    // name, so "Book club" would also find "Book club 2".)
    // A group since deleted, or a tag no one has any more, no longer filters (the menu shows "Any").
    const members = tree.groups[group] ? new Set(tree.groups[group].members.map((m) => m.personId)) : null;
    const exactTag = allTags.includes(tag) ? tag : '';
    return found.filter((r) => (!members || members.has(r.person.id)) && (!exactTag || r.person.tags.includes(exactTag)));
  }, [tree, deferred, group, tag, allTags]);

  const sorted = useMemo(() => {
    if (sort.key === 'relevance') return sort.dir === 1 ? results : [...results].reverse();
    const k = sort.key;
    // null = nothing to sort by; those rows stay at the bottom whichever way the column is sorted.
    const key = (p: Person): string | number | null => {
      if (k === 'born' || k === 'died') {
        const v = dateSortKey(k === 'born' ? birthDate(p) : deathDate(p));
        return Number.isFinite(v) ? v : null;
      }
      if (k === 'age') return ageOf(p)?.age ?? null;
      if (k === 'birthday') return (!isDeceased(p) ? nextAnniversary(birthDate(p))?.days : undefined) ?? null;
      if (k === 'name') return 0;
      return cellText(tree, p, k).toLowerCase() || null;
    };
    return [...results].sort((a, b) => {
      if (k === 'name') return comparePeople(a.person, b.person) * sort.dir;
      if (k === 'relation') {
        // Closest relatives first; people with no connection last.
        const ra = homeRelation(tree, a.person.id);
        const rb = homeRelation(tree, b.person.id);
        if (!ra || !rb) return ra ? -1 : rb ? 1 : 0;
        return (ra.rank - rb.rank || ra.steps - rb.steps || ra.term.localeCompare(rb.term)) * sort.dir;
      }
      const ka = key(a.person);
      const kb = key(b.person);
      if (ka === null || kb === null) return ka === null ? (kb === null ? 0 : 1) : -1;
      return (ka < kb ? -1 : ka > kb ? 1 : 0) * sort.dir;
    });
  }, [tree, results, sort]);

  // Ticked people in table order; those the current search hides come last.
  const picked = useMemo(() => {
    const shownIds = sorted.filter((r) => checked.has(r.person.id)).map((r) => r.person.id);
    const inList = new Set(shownIds);
    return { ids: [...shownIds, ...[...checked].filter((id) => tree.people[id] && !inList.has(id))], hidden: [...checked].filter((id) => tree.people[id] && !inList.has(id)).length };
  }, [sorted, checked, tree]);
  const allShown = sorted.length > 0 && sorted.every((r) => checked.has(r.person.id));
  const someShown = sorted.some((r) => checked.has(r.person.id));
  const headCheck = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (headCheck.current) headCheck.current.indeterminate = someShown && !allShown;
  });
  /** Tick or untick someone; with Shift, everyone from the last one clicked to here. */
  const toggleCheck = (id: Id, range: boolean) => {
    const ids = sorted.map((r) => r.person.id);
    const from = anchor.current ? ids.indexOf(anchor.current) : -1;
    const to = ids.indexOf(id);
    const span = range && from >= 0 && to >= 0 ? ids.slice(Math.min(from, to), Math.max(from, to) + 1) : [id];
    const on = !checked.has(id);
    const next = new Set(checked);
    for (const x of span) if (on) next.add(x); else next.delete(x);
    setChecked(next);
    anchor.current = id;
  };
  const toggleAllShown = () => {
    const next = new Set(checked);
    for (const r of sorted) if (allShown) next.delete(r.person.id); else next.add(r.person.id);
    setChecked(next);
  };
  const clearChecks = () => setChecked(new Set());
  const editChecked = () => openDialog({ type: 'bulk-edit', personIds: picked.ids });
  const mergeChecked = () => openDialog({ type: 'merge', personId: picked.ids[0], otherId: picked.ids[1] });
  const deleteChecked = () => {
    const ids = picked.ids;
    const who = plural(ids.length, 'person', 'people');
    confirmAction({
      title: `Delete ${who}?`,
      body: `This removes ${ids.length <= 3 ? joinNames(ids.map((id) => displayName(tree.people[id]))) : `these ${ids.length.toLocaleString('en-US')} people`}${picked.hidden ? ` (${picked.hidden.toLocaleString('en-US')} of them not in the current list)` : ''}, along with their relationships and group memberships. You can undo this right after.`,
      confirmLabel: `Delete ${who}`,
      danger: true,
      onConfirm: () => {
        commit(removePeople(getState().tree!, ids));
        clearChecks();
        toast(`Deleted ${who}.`, 'info', { label: 'Undo', run: undo });
      },
    });
  };
  const bulkMenu = (): MenuEntry[] => [
    { key: 'edit', label: `Edit ${plural(picked.ids.length, 'person', 'people')}…`, icon: <Pencil size={14} />, onSelect: editChecked },
    ...(picked.ids.length === 2 ? [{ key: 'merge', label: 'Merge the two…', icon: <Merge size={14} />, onSelect: mergeChecked }] : []),
    { key: 'clear', label: 'Clear selection', icon: <X size={14} />, onSelect: clearChecks },
    'separator',
    { key: 'delete', label: `Delete ${plural(picked.ids.length, 'person', 'people')}…`, icon: <Trash2 size={14} />, danger: true, onSelect: deleteChecked },
  ];
  // Escape clears the selection (when not typing or in a dialog).
  useEffect(() => {
    if (!checked.size) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || getState().dialogs.length || (e.target as HTMLElement).closest?.('input, textarea, select, [contenteditable], [role=menu]')) return;
      setChecked(new Set());
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [checked]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [view, setView] = useState({ w: 900, h: 600 });
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setView({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [deferred, group, tag]);

  const terms = useMemo(() => normalize(query.replace(/-?\w+:("[^"]*"|\S+)/g, '')).split(' ').filter((t) => t.length > 1), [query]);
  const filterMenu = usePopover();
  const clearFilters = () => {
    setStatus('');
    setGender('');
    setBornFrom('');
    setBornTo('');
    setGroup('');
    setTag('');
  };
  /** The filters in use, as chips under the search box. */
  const activeFilters: { key: string; label: string; clear: () => void }[] = [
    ...(status ? [{ key: 'status', label: status === 'unknown' ? 'Living status not recorded' : status === 'living' ? 'Living' : 'Deceased', clear: () => setStatus('') }] : []),
    ...(gender ? [{ key: 'gender', label: GENDER_LABELS[gender as GenderIdentity] ?? gender, clear: () => setGender('') }] : []),
    ...(bornFrom || bornTo ? [{ key: 'born', label: bornFrom && bornTo ? `Born ${bornFrom}–${bornTo}` : bornFrom ? `Born ${bornFrom} or later` : `Born ${bornTo} or earlier`, clear: () => (setBornFrom(''), setBornTo('')) }] : []),
    ...(tree.groups[group] ? [{ key: 'group', label: tree.groups[group].name, clear: () => setGroup('') }] : []),
    ...(tag && allTags.includes(tag) ? [{ key: 'tag', label: `Tagged ${tag}`, clear: () => setTag('') }] : []),
  ];
  const shown = COLUMNS.filter((c) => columns.includes(c.key));

  // Column widths fitted to everyone's data (not just the visible rows, or columns would jump
  // while scrolling), each within its min/max. Spare room goes to Notes when it's shown.
  const fitted = useMemo(() => {
    const people = Object.values(tree.people);
    const body = font(13.5);
    const head = font(12, 650);
    const fit = (texts: string[], label: string, min: number, max: number, f = body, extra = 0, lock = false) => {
      let w = textWidth(label, head) + 18 + (lock ? 15 : 0); // the sort arrow and read-only lock
      for (const t of texts) w = Math.max(w, textWidth(t, f) + extra);
      return Math.round(Math.min(max, Math.max(min, w + CELL_PAD)));
    };
    const widths: Record<string, number> = {
      check: CHECK_W,
      name: fit(people.map((p) => displayName(p)), 'Best match', 170, 320, font(13.5, 600), NAME_EXTRA),
    };
    // How narrow each column may get.
    const mins: Record<string, number> = {};
    for (const c of shown) {
      mins[c.key] = c.min;
      if (c.key === 'notes') widths.notes = NOTES_PREFERRED;
      else if (c.key === 'relation') widths.relation = RELATION_PREFERRED;
      else if (isKin(c.key) || c.key === 'groups') {
        // Chips sit side by side and wrap between chips, never inside one, so the column is
        // as wide as the longest row of chips and no narrower than the widest chip.
        const chipFont = font(12.5, 600);
        let w = textWidth(c.label, head) + 18;
        let longest = 0;
        for (const p of people) {
          const chips = isKin(c.key)
            ? kin(tree, p, c.key).map((id) => textWidth(shortName(tree.people[id]), chipFont) + PERSON_CHIP)
            : (graphOf(tree).groupsOf.get(p.id) ?? []).map((g) => textWidth(g.name, chipFont) + GROUP_CHIP);
          if (!chips.length) continue;
          w = Math.max(w, chips.reduce((a, b) => a + b, 0) + CHIP_GAP * (chips.length - 1));
          longest = Math.max(longest, ...chips);
        }
        mins[c.key] = Math.min(c.max, Math.max(c.min, Math.ceil(longest) + CELL_PAD));
        widths[c.key] = Math.max(mins[c.key], Math.round(Math.min(c.max, w + CELL_PAD)));
      } else widths[c.key] = fit(people.map((p) => (c.key === 'died' && !deathDate(p) && isDeceased(p) ? 'deceased' : cellText(tree, p, c.key))), c.label, c.min, c.max, body, 0, !c.editable);
    }
    return { widths, mins };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree, columns]);
  const widths = { ...fitted.widths };
  const sum = () => Object.values(widths).reduce((a, b) => a + b, 0);
  const minOf = (k: string) => fitted.mins[k];
  if (sum() > view.w) {
    // Too wide: wrapping columns shrink in proportion to how much they can give, down to their minimums.
    const flex = FLEXIBLE.filter((k) => widths[k] !== undefined);
    const room = flex.reduce((n, k) => n + (widths[k] - minOf(k)), 0);
    const cut = Math.min(sum() - view.w, room);
    if (room > 0) for (const k of flex) widths[k] = Math.round(widths[k] - ((widths[k] - minOf(k)) / room) * cut);
  } else if (widths.notes !== undefined) {
    widths.notes = Math.min(COLUMNS.find((c) => c.key === 'notes')!.max, widths.notes + (view.w - sum()));
  }
  const tableW = Math.max(view.w, sum());

  // Rows are as tall as their notes need. Heights are estimated from the text, then corrected
  // from what actually rendered, so scrolling through thousands of people stays smooth.
  const measured = useRef(new Map<string, number>());
  const [, setMeasureTick] = useState(0);
  // Different widths mean different wrapping, so earlier measurements no longer apply.
  const layoutKey = `${widths.notes ?? 0}|${columns.join()}|${view.w}`;
  const measuredFor = useRef(layoutKey);
  if (measuredFor.current !== layoutKey) {
    measured.current.clear();
    measuredFor.current = layoutKey;
  }
  const notesW = widths.notes !== undefined ? widths.notes - CELL_PAD : 0;
  const estimate = (p: Person) => (notesW && p.notes ? Math.max(ROW_H, Math.ceil(lineCount(p.notes, notesW, font(13.5)) * LINE_H) + 15) : ROW_H);
  const offsets = new Array<number>(sorted.length + 1);
  offsets[0] = 0;
  for (let i = 0; i < sorted.length; i++) offsets[i + 1] = offsets[i] + (measured.current.get(sorted[i].person.id) ?? estimate(sorted[i].person));
  const firstAt = (y: number) => {
    let lo = 0;
    let hi = sorted.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (offsets[mid + 1] <= y) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  };
  const start = Math.max(0, firstAt(scrollTop) - 8);
  const end = Math.min(sorted.length, firstAt(scrollTop + view.h) + 9);
  const tbodyRef = useRef<HTMLTableSectionElement>(null);
  useLayoutEffect(() => {
    let changed = false;
    for (const tr of tbodyRef.current?.querySelectorAll<HTMLTableRowElement>('tr[data-person-id]') ?? []) {
      const id = tr.dataset.personId!;
      const h = tr.getBoundingClientRect().height;
      if (Math.abs((measured.current.get(id) ?? -1) - h) > 0.5) {
        measured.current.set(id, h);
        changed = true;
      }
    }
    if (changed) setMeasureTick((x) => x + 1);
  });

  const save = (person: Person) => {
    const cur = getState().tree!.people[person.id];
    if (cur && JSON.stringify(cur) !== JSON.stringify(person)) commit(upsertPerson(getState().tree!, person));
  };
  /** Move to the next editable cell in the row (Tab), or stop editing. */
  const editNext = (id: string, from: ColKey | 'name', dir: 1 | -1) => {
    const order: (ColKey | 'name')[] = ['name', ...shown.filter((c) => c.editable).map((c) => c.key)];
    const next = order[order.indexOf(from) + dir];
    setEditing(next ? { id, key: next } : null);
  };

  const header = (key: SortKey, label: string, title?: string, readOnly?: boolean) => (
    <th key={key} aria-sort={sort.key === key ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}>
      <button title={[title, readOnly && READ_ONLY_HINT].filter(Boolean).join('. ') || undefined} onClick={() => setSort((s) => ({ key, dir: s.key === key ? ((-s.dir) as 1 | -1) : 1 }))}>
        {readOnly && <Lock size={11} className="readonly-icon" aria-label="Read-only" />}
        {label}
        {sort.key === key && (sort.dir === 1 ? <ArrowDown size={12} /> : <ArrowUp size={12} />)}
      </button>
    </th>
  );

  return (
    <div className="people-view">
      <div className="view-toolbar" style={{ flexDirection: 'column', alignItems: 'stretch', flexWrap: 'nowrap' }}>
        <div className="row">
          <div className="search-box">
            <Search size={16} />
            <input
              ref={inputRef}
              className="input"
              type="search"
              placeholder="Search names, places, dates, notes…"
              value={query}
              onChange={(e) => setState({ peopleQuery: e.target.value })}
              aria-label="Search people"
              data-global-search
            />
          </div>
          <button className="btn" aria-haspopup="dialog" aria-expanded={filterMenu.open} onClick={filterMenu.toggle} aria-label={`Filters${activeFilters.length ? `, ${activeFilters.length} on` : ''}`} title="Filter by living status, gender, birth year, group, or tag">
            <ListFilter size={15} /> <span className="hide-sm">Filters</span>
            {activeFilters.length > 0 && <span className="count-badge">{activeFilters.length}</span>}
          </button>
          <button className={`btn icon${help ? ' primary' : ''}`} onClick={() => setHelp((h) => !h)} aria-label="Search tips" aria-expanded={help} title="Search tips">
            <HelpCircle size={16} />
          </button>
          <button
            className="btn"
            aria-haspopup="menu"
            aria-expanded={!!colMenu}
            aria-label="Columns"
            title="Choose columns"
            onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              setColMenu(colMenu ? null : { x: r.left, y: r.bottom + 4 });
            }}
          >
            <Columns3 size={15} /> <span className="hide-sm">Columns</span>
          </button>
          <button className="btn" onClick={() => openDialog({ type: 'new-person' })} aria-label="Add person" title="Add a person (N)">
            <UserPlus size={15} /> <span className="hide-sm">Add person</span>
          </button>
        </div>
        {/* What's filtering the list, each removable, so the filters themselves can stay tucked away. */}
        {activeFilters.length > 0 && (
          <div className="filter-chips" role="group" aria-label="Filters on">
            {activeFilters.map((f) => (
              <span key={f.key} className="chip">
                {f.label}
                <button onClick={f.clear} aria-label={`Remove filter: ${f.label}`}>
                  <X size={12} />
                </button>
              </span>
            ))}
            {activeFilters.length > 1 && (
              <button className="link-btn small-text" onClick={clearFilters}>
                Clear all
              </button>
            )}
          </div>
        )}
        {colMenu && (
          <Menu
            ariaLabel="Columns"
            x={colMenu.x}
            y={colMenu.y}
            onClose={() => setColMenu(null)}
            items={[
              { key: 'name', label: 'Name', checked: true, disabled: true, keepOpen: true },
              ...COLUMNS.flatMap((c): MenuEntry[] => [
                ...(MENU_BREAKS.includes(c.key) ? ['separator' as const] : []),
                {
                  key: c.key,
                  label: c.label,
                  hint: c.key === 'relation' && !hasHome ? 'needs a home person' : c.editable ? undefined : 'read-only',
                  checked: columns.includes(c.key),
                  keepOpen: true,
                  onSelect: () => toggleColumn(c.key),
                },
              ]),
              'separator',
              { key: 'reset', label: 'Reset to default', onSelect: () => { setColumns(DEFAULT_COLUMNS); lsSet(COLS_KEY, JSON.stringify(DEFAULT_COLUMNS)); } },
            ]}
          />
        )}
        {filterMenu.open && (
          <Popover anchor={filterMenu.anchor!} onClose={filterMenu.close} ariaLabel="Filters">
            <div className="filter-grid">
              <label htmlFor="pf-status">Living status</label>
              <select id="pf-status" className="select" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
                <option value="">Any</option>
                <option value="living">Living</option>
                <option value="deceased">Deceased</option>
                <option value="unknown">Not recorded</option>
              </select>
              <label htmlFor="pf-gender">Gender</label>
              <select id="pf-gender" className="select" value={gender} onChange={(e) => setGender(e.target.value)}>
                <option value="">Any</option>
                {(Object.keys(GENDER_LABELS) as GenderIdentity[]).map((g) => (
                  <option key={g} value={g}>
                    {GENDER_LABELS[g]}
                  </option>
                ))}
              </select>
              <label htmlFor="pf-born">Born</label>
              <span className="row" style={{ gap: 6 }}>
                <input id="pf-born" className="input" inputMode="numeric" placeholder="from" value={bornFrom} onChange={(e) => setBornFrom(e.target.value.replace(/\D/g, '').slice(0, 4))} aria-label="Born from year" />
                <span className="muted">–</span>
                <input className="input" inputMode="numeric" placeholder="to" value={bornTo} onChange={(e) => setBornTo(e.target.value.replace(/\D/g, '').slice(0, 4))} aria-label="Born to year" />
              </span>
              {groups.length > 0 && (
                <>
                  <label htmlFor="pf-group">Group</label>
                  <select id="pf-group" className="select" value={group} onChange={(e) => setGroup(e.target.value)}>
                    <option value="">Any</option>
                    {groups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                </>
              )}
              {allTags.length > 0 && (
                <>
                  <label htmlFor="pf-tag">Tag</label>
                  <select id="pf-tag" className="select" value={tag} onChange={(e) => setTag(e.target.value)}>
                    <option value="">Any</option>
                    {allTags.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </>
              )}
            </div>
            <div className="popover-foot row">
              <span>
                {sorted.length.toLocaleString('en-US')} {sorted.length === 1 ? 'person' : 'people'}
              </span>
              <span className="spacer" style={{ flex: 1 }} />
              {activeFilters.length > 0 && (
                <button className="link-btn" onClick={clearFilters}>
                  Clear filters
                </button>
              )}
            </div>
          </Popover>
        )}
        {help && (
          <div className="callout" style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 14px' }}>
            {SEARCH_HELP.map((h) => (
              <div key={h.syntax} style={{ display: 'contents' }}>
                <code style={{ cursor: 'pointer' }} onClick={() => setState({ peopleQuery: (query ? query + ' ' : '') + h.syntax.split('  ')[0] })} title="Add to search">
                  {h.syntax}
                </code>
                <span className="muted">{h.meaning}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {picked.ids.length > 0 ? (
        <div className="result-count bulk-bar" role="toolbar" aria-label="Selected people">
          <span aria-live="polite">
            <b>
              {picked.hidden ? plural(picked.ids.length, 'person', 'people') : picked.ids.length.toLocaleString('en-US')}
              {!picked.hidden && <span className="hide-sm"> of {plural(sorted.length, 'person', 'people')}</span>} selected
            </b>
            {picked.hidden > 0 && <span className="faint"> · {picked.hidden.toLocaleString('en-US')} not in this list</span>}
          </span>
          <button className="btn small" onClick={editChecked}>
            <Pencil size={14} /> Edit…
          </button>
          {picked.ids.length === 2 && (
            <button className="btn small" onClick={mergeChecked} aria-label="Merge…" title="Combine two records of the same person">
              <Merge size={14} /> <span className="hide-sm">Merge…</span>
            </button>
          )}
          <button className="btn small danger" onClick={deleteChecked} aria-label="Delete…">
            <Trash2 size={14} /> <span className="hide-sm">Delete…</span>
          </button>
          <span className="spacer" style={{ flex: 1 }} />
          <button className="btn small ghost" onClick={clearChecks} aria-label="Clear selection" title="Clear selection (Esc)">
            <X size={14} /> <span className="hide-sm">Clear selection</span>
          </button>
        </div>
      ) : (
      <div className="result-count" aria-live="polite">
        {sorted.length.toLocaleString('en-US')} {sorted.length === 1 ? 'person' : 'people'}
        {fullQuery.trim() || group || tag ? ` match` : ''}
        {fullQuery.trim() && sorted.length > 0 && sort.key === 'relevance' && <span className="faint">· best matches first</span>}
        <span className="spacer" style={{ flex: 1 }} />
        <span className="faint hide-sm row" style={{ gap: 4 }}>
          Click a cell to edit it, or tick people to change several at once. Columns marked <Lock size={11} aria-label="with a lock" /> are worked out from the tree.
        </span>
      </div>
      )}
      <div className="table-wrap" ref={scrollRef} onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}>
        {sorted.length === 0 && !Object.keys(tree.people).length ? (
          <Empty title="No one in this tree yet">
            <button className="btn primary" onClick={() => openDialog({ type: 'new-person' })}>
              <UserPlus size={16} /> Add the first person
            </button>
          </Empty>
        ) : sorted.length === 0 ? (
          <div className="empty">
            <div>
              <h3>No matches</h3>
              <p style={{ marginTop: 6 }}>Try fewer words, check spelling, or remove a filter.</p>
            </div>
          </div>
        ) : (
          <table className="ptable" style={{ width: tableW }}>
            <colgroup>
              <col style={{ width: widths.check }} />
              <col style={{ width: widths.name }} />
              {shown.map((c) => (
                <col key={c.key} style={{ width: widths[c.key] }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th className="check-cell">
                  <input ref={headCheck} type="checkbox" checked={allShown} onChange={toggleAllShown} aria-label={allShown ? 'Clear selection of everyone shown' : `Select all ${plural(sorted.length, 'person', 'people')} shown`} title={allShown ? 'Clear selection' : 'Select everyone shown'} />
                </th>
                {header(fullQuery.trim() ? 'relevance' : 'name', fullQuery.trim() ? 'Best match' : 'Name')}
                {shown.map((c) => header(c.key, c.label, c.title, !c.editable))}
              </tr>
            </thead>
            <tbody ref={tbodyRef}>
              {start > 0 && <tr style={{ height: offsets[start] }} aria-hidden />}
              {sorted.slice(start, end).map((r) => {
                const p = r.person;
                const extra = r.matches.filter((m) => m.field !== 'name' && m.field !== 'year');
                const edit = editing?.id === p.id ? editing.key : null;
                // One click edits a cell (and, bubbling to the row, selects the person).
                const cellProps = (key: ColKey | 'name') => {
                  const editable = key === 'name' || !!COLUMNS.find((c) => c.key === key)?.editable;
                  return {
                    onClick: editable && edit !== key ? () => setEditing({ id: p.id, key }) : undefined,
                    className: editable ? `editable${key === 'gender' || key === 'pronouns' || key === 'groups' || isKin(key) ? ' pick' : ''}` : undefined,
                  };
                };
                const done = (person: Person | null, move?: 1 | -1) => {
                  if (person) save(person);
                  if (move) editNext(p.id, edit!, move);
                  else {
                    setEditing(null);
                    // Enter or Escape leaves focus on the row, so the keyboard keeps its place
                    // (unless focus already went somewhere else, as on a click away).
                    requestAnimationFrame(() => {
                      if (document.activeElement === document.body) tbodyRef.current?.querySelector<HTMLElement>(`tr[data-person-id="${p.id}"]`)?.focus();
                    });
                  }
                };
                return (
                  <tr
                    key={p.id}
                    className={[p.id === selectedId && 'selected', checked.has(p.id) && 'checked'].filter(Boolean).join(' ') || undefined}
                    style={{ height: ROW_H }}
                    onClick={() => setSelectedId(p.id)}
                    tabIndex={0}
                    data-person-id={p.id}
                    aria-selected={checked.has(p.id)}
                    onKeyDown={(e) => {
                      if (e.target !== e.currentTarget) return;
                      if (e.key === 'Enter') setSelectedId(p.id);
                      else if (e.key === 'F2') setEditing({ id: p.id, key: 'name' });
                      else if (e.key === ' ') {
                        e.preventDefault();
                        toggleCheck(p.id, e.shiftKey);
                      }
                    }}
                    onContextMenu={(e) => {
                      // Right-clicking one of several ticked people acts on all of them.
                      if (checked.has(p.id) && picked.ids.length > 1) openContextMenu(e, bulkMenu());
                    }}
                  >
                    <td
                      className="check-cell"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCheck(p.id, e.shiftKey);
                      }}
                    >
                      <input type="checkbox" checked={checked.has(p.id)} onChange={() => {}} aria-label={`Select ${displayName(p)}`} tabIndex={-1} />
                    </td>
                    <td {...cellProps('name')}>
                      {edit === 'name' && (
                        <div className="cell-editor">
                          <NameEditor person={p} onDone={done} />
                        </div>
                      )}
                      {/* While editing, the normal content stays (invisibly) so the column keeps its width. */}
                      <div className={edit === 'name' ? 'cell-ghost' : undefined} aria-hidden={edit === 'name' || undefined}>
                        <div className="row" style={{ gap: 10 }}>
                          <button
                            className="avatar-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedId(p.id);
                              showPerson(p.id);
                            }}
                            aria-label={`Show details for ${displayName(p)}`}
                            title="Show details"
                          >
                            <Avatar person={p} size="sm" />
                          </button>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600 }}>
                              <Highlight text={displayName(p)} terms={terms} />
                            </div>
                            {extra.length > 0 && (
                              <div className="match">
                                {extra.slice(0, 2).map((m, i) => (
                                  <span key={i}>
                                    {i > 0 && ' · '}
                                    {FIELD_LABEL[m.field]}: <Highlight text={snippet(m.text, terms)} terms={terms} />
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    {shown.map((c) => {
                      // Lists edit in a panel under the cell, leaving the cell as it is.
                      const listCol = c.key === 'groups' || isKin(c.key);
                      const ghost = edit === c.key && !listCol;
                      return (
                        <td key={c.key} {...cellProps(c.key)}>
                          {edit === c.key &&
                            (c.key === 'groups' ? (
                              <GroupsEditor person={p} onDone={(move) => done(null, move)} />
                            ) : isKin(c.key) ? (
                              <KinEditor person={p} col={c.key} onDone={(move) => done(null, move)} />
                            ) : (
                              <div className="cell-editor">
                                <CellEditor tree={tree} person={p} col={c.key} onDone={done} />
                              </div>
                            ))}
                          <span className={ghost ? 'cell-ghost' : undefined} aria-hidden={ghost || undefined}>
                            <CellView tree={tree} person={p} col={c.key} />
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {end < sorted.length && <tr style={{ height: offsets[sorted.length] - offsets[end] }} aria-hidden />}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function CellView({ tree, person: p, col }: { tree: Tree; person: Person; col: ColKey }): ReactNode {
  const text = cellText(tree, p, col);
  if (col === 'died' && !text && isDeceased(p)) return <span className="faint">deceased</span>;
  if (col === 'notes') return <span className="cell-notes muted">{text}</span>;
  if (col === 'birthday' && text) {
    const b = nextAnniversary(birthDate(p))!;
    return (
      <span className={b.days <= 7 ? undefined : 'muted'}>
        {inDays(b.days)} <span className="faint">· turns {b.count}</span>
      </span>
    );
  }
  if (col === 'relation' && !text && tree.homePersonId && tree.homePersonId !== p.id) return <span className="faint">not connected</span>;
  if (isKin(col)) {
    const ids = kin(tree, p, col);
    if (!ids.length) return null;
    return (
      <span className="chip-list">
        {ids.map((id) => (
          <button
            key={id}
            className="person-chip"
            data-person-id={id}
            onClick={(e) => {
              e.stopPropagation();
              showPerson(id);
            }}
            title={`Show details for ${displayName(tree.people[id])}`}
          >
            <Avatar person={tree.people[id]} size="sm" />
            {shortName(tree.people[id])}
          </button>
        ))}
      </span>
    );
  }
  if (col === 'groups') {
    const groups = graphOf(tree).groupsOf.get(p.id) ?? [];
    if (!groups.length) return null;
    return (
      <span className="chip-list">
        {groups.map((g) => (
          <button
            key={g.id}
            className="group-chip group-tint"
            style={{ '--gc': g.color } as React.CSSProperties}
            onClick={(e) => {
              e.stopPropagation();
              openDialog({ type: 'group-details', groupId: g.id });
            }}
            title={`Show ${g.name}`}
          >
            {g.name}
          </button>
        ))}
      </span>
    );
  }
  if (col === 'birthplace' || col === 'deathplace' || col === 'residence' || col === 'gender' || col === 'pronouns') return <span className="muted">{text}</span>;
  return text;
}

type Done = (person: Person | null, move?: 1 | -1) => void;

/** Keys shared by every cell editor: Enter saves, Tab saves and moves, Escape cancels. */
function editKeys(e: React.KeyboardEvent, commitValue: () => Person, onDone: Done) {
  e.stopPropagation();
  if (e.key === 'Enter' && !(e.shiftKey && e.currentTarget.tagName === 'TEXTAREA')) {
    e.preventDefault();
    onDone(commitValue());
  } else if (e.key === 'Tab') {
    e.preventDefault();
    onDone(commitValue(), e.shiftKey ? -1 : 1);
  } else if (e.key === 'Escape') {
    e.preventDefault();
    onDone(null);
  }
}

function CellEditor({ tree, person: p, col, onDone }: { tree: Tree; person: Person; col: ColKey; onDone: Done }) {
  const [initial] = useState(() => (col === 'born' || col === 'died' ? formatDate(col === 'born' ? birthDate(p) : deathDate(p), true) : cellText(tree, p, col)));
  const [value, setValue] = useState(initial);
  // Leaving a cell as it was changes nothing (a date shown differently from how it's stored stays as stored).
  const result = () => (value === initial ? p : applyEdit(p, col, value));
  const common = {
    autoFocus: true,
    className: 'input cell-input',
    value,
    'aria-label': COLUMNS.find((c) => c.key === col)?.label,
    onClick: (e: React.MouseEvent) => e.stopPropagation(),
    onBlur: () => onDone(result()),
  };
  if (col === 'gender') {
    return (
      <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.key === 'Escape' && onDone(null)}>
        <GenderPicker value={p.gender.identity} onChange={(identity) => onDone({ ...p, gender: { ...p.gender, identity }, pronouns: p.pronouns || defaultPronouns(identity) })} ariaLabel="Gender" autoOpen onClose={() => onDone(null)} />
      </span>
    );
  }
  if (col === 'pronouns') {
    return (
      <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.key === 'Escape' && onDone(null)}>
        <PronounPicker value={p.pronouns} onChange={(pronouns) => onDone({ ...p, pronouns })} ariaLabel="Pronouns" autoOpen onClose={() => onDone(null)} />
      </span>
    );
  }
  if (col === 'notes') {
    return <textarea {...common} rows={1} onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => editKeys(e, result, onDone)} />;
  }
  if (col === 'birthplace' || col === 'deathplace' || col === 'residence') {
    // Choosing a suggestion saves the cell, as Enter does.
    return <PlaceInput {...common} onChange={setValue} onKeyDown={(e) => editKeys(e, result, onDone)} onPick={(place) => onDone(place === initial ? p : applyEdit(p, col, place))} />;
  }
  const hint = col === 'born' || col === 'died' ? (value.trim() ? parseUserDate(value) : null) : null;
  return (
    <>
      <input
        {...common}
        placeholder={col === 'tags' ? 'tag, another tag' : col === 'born' || col === 'died' ? 'e.g., March 3, 1950' : undefined}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => editKeys(e, result, onDone)}
        title={hint ? (hint.understood ? `Will be saved as ${formatDate(hint.value, true)}` : "Couldn't read this as a date; it'll be kept as written.") : undefined}
      />
    </>
  );
}

function NameEditor({ person: p, onDone }: { person: Person; onDone: Done }) {
  const idx = p.names.findIndex((n) => n === primaryName(p));
  const n = p.names[idx] ?? { given: '', surname: '', type: 'name' as const };
  const [given, setGiven] = useState(n.given);
  const [surname, setSurname] = useState(n.surname);
  const wrap = useRef<HTMLDivElement>(null);
  const result = (): Person => {
    const name = { ...n, given: given.trim(), surname: surname.trim() };
    return { ...p, names: idx >= 0 ? p.names.map((x, i) => (i === idx ? name : x)) : [name, ...p.names] };
  };
  const keys = (e: React.KeyboardEvent<HTMLInputElement>, last: boolean) => {
    // Tab between the two name fields stays inside the editor.
    if (e.key === 'Tab' && ((!last && !e.shiftKey) || (last && e.shiftKey))) return e.stopPropagation();
    editKeys(e, result, onDone);
  };
  return (
    <div
      ref={wrap}
      className="row"
      style={{ gap: 4 }}
      onClick={(e) => e.stopPropagation()}
      onBlur={(e) => {
        if (!wrap.current?.contains(e.relatedTarget as Node)) onDone(result());
      }}
    >
      <input className="input cell-input" autoFocus aria-label="First and middle names" placeholder="First" value={given} onChange={(e) => setGiven(e.target.value)} onKeyDown={(e) => keys(e, false)} />
      <input className="input cell-input" aria-label="Last name" placeholder="Last" value={surname} onChange={(e) => setSurname(e.target.value)} onKeyDown={(e) => keys(e, true)} />
    </div>
  );
}

function snippet(text: string, terms: string[]): string {
  if (text.length <= 80) return text;
  const n = normalize(text);
  const idx = terms.map((t) => n.indexOf(t)).filter((i) => i >= 0).sort((a, b) => a - b)[0] ?? 0;
  const s = Math.max(0, idx - 30);
  return (s > 0 ? '…' : '') + text.slice(s, s + 80) + (s + 80 < text.length ? '…' : '');
}

/** Highlight query terms, matching accent-insensitively. */
function Highlight({ text, terms }: { text: string; terms: string[] }) {
  if (!terms.length) return <>{text}</>;
  // Build a normalized copy with a per-character index map back to the original.
  let norm = '';
  const map: number[] = [];
  for (let i = 0; i < text.length; i++) {
    const c = text[i].normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    for (const ch of c) {
      norm += ch;
      map.push(i);
    }
  }
  const marks = new Array(text.length).fill(false);
  for (const t of terms) {
    let from = 0;
    let idx: number;
    while ((idx = norm.indexOf(t, from)) >= 0) {
      for (let k = idx; k < idx + t.length; k++) marks[map[k]] = true;
      from = idx + t.length;
    }
  }
  const out: JSX.Element[] = [];
  let i = 0;
  while (i < text.length) {
    const on = marks[i];
    let j = i;
    while (j < text.length && marks[j] === on) j++;
    out.push(on ? <mark key={i}>{text.slice(i, j)}</mark> : <span key={i}>{text.slice(i, j)}</span>);
    i = j;
  }
  return <>{out}</>;
}
